import { useState, useEffect } from "react";
import { Plus, Trash2, Shield, AlertTriangle, AlertOctagon, Search, ArrowRightLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: Shield },
    medium: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertTriangle },
    high: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: AlertOctagon }
  };
  const { bg, text, border, icon: Icon } = config[level] || config.low;
  
  return (
    <Badge className={`${bg} ${text} ${border} border`}>
      <Icon className="w-3 h-3 mr-1" />
      {level}
    </Badge>
  );
};

const networks = [
  "ETH", "BTC", "TRX", "BSC", "POLYGON", "ARBITRUM", "OPTIMISM", "AVAX", "SOL",
  "BASE", "LINEA", "ZKSYNC", "FANTOM", "CRONOS", "HARMONY", "CELO", "MOONBEAM",
  "NEAR", "COSMOS", "POLKADOT", "CARDANO", "ALGORAND", "STELLAR", "RIPPLE",
  "LITECOIN", "DOGECOIN", "TON", "TONCOIN", "APTOS", "SUI", "SEI", "INJECTIVE",
  "MANTLE", "SCROLL", "BLAST", "MODE", "MANTA", "STARKNET", "HEDERA"
];
const tokens = [
  "ETH", "BTC", "USDT", "USDC", "TRX", "BNB", "MATIC", "SOL", "AVAX",
  "ARB", "OP", "BASE", "LINK", "UNI", "AAVE", "CRV", "LDO", "APE",
  "DOGE", "SHIB", "PEPE", "WIF", "BONK", "FLOKI", "TON", "APT", "SUI",
  "INJ", "SEI", "TIA", "PYTH", "JUP", "STRK", "ZK", "MANTA", "BLAST"
];

const AdminTransactions = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [date, setDate] = useState(new Date());
  
  const [formData, setFormData] = useState({
    tx_hash: "",
    from_address: "",
    to_address: "",
    amount: "",
    token: "ETH",
    network: "ETH",
    risk_level: "low",
    notes: ""
  });

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки транзакций");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const openCreateDialog = () => {
    setFormData({
      tx_hash: "",
      from_address: "",
      to_address: "",
      amount: "",
      token: "ETH",
      network: "ETH",
      risk_level: "low",
      notes: ""
    });
    setDate(new Date());
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        timestamp: date.toISOString()
      };

      await axios.post(`${API}/admin/transactions`, payload, { withCredentials: true });
      toast.success("Транзакция создана");
      
      setIsDialogOpen(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка сохранения");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/transactions/${deleteId}`, { withCredentials: true });
      toast.success("Транзакция удалена");
      setDeleteId(null);
      fetchTransactions();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const filteredTransactions = transactions.filter(tx => 
    tx.tx_hash.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.from_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tx.to_address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout user={user}>
      <div className="space-y-6" data-testid="admin-transactions">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Транзакции</h1>
            <p className="text-slate-500">Управление транзакциями</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-tx-btn">
            <Plus className="w-4 h-4 mr-2" />
            Добавить транзакцию
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Поиск по хешу или адресу..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-transactions"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              {searchQuery ? "Транзакции не найдены" : "Нет транзакций. Добавьте первую."}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map((tx) => (
              <Card key={tx.tx_id} className="card-hover" data-testid={`tx-${tx.tx_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="font-mono text-xs">{tx.network}</Badge>
                        <RiskBadge level={tx.risk_level} />
                        <span className="text-xs text-slate-500">
                          {new Date(tx.timestamp).toLocaleDateString("ru-RU")}
                        </span>
                      </div>
                      <p className="font-mono text-xs text-slate-500 truncate mb-2">
                        Hash: {tx.tx_hash}
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-slate-600 truncate max-w-[200px]">
                          {tx.from_address.slice(0, 10)}...{tx.from_address.slice(-6)}
                        </span>
                        <ArrowRightLeft className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="font-mono text-slate-600 truncate max-w-[200px]">
                          {tx.to_address.slice(0, 10)}...{tx.to_address.slice(-6)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">
                          {tx.amount} {tx.token}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteId(tx.tx_id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-${tx.tx_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Добавить транзакцию</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Хеш транзакции *</Label>
                <Input
                  placeholder="0x..."
                  value={formData.tx_hash}
                  onChange={(e) => setFormData({ ...formData, tx_hash: e.target.value })}
                  className="font-mono"
                  data-testid="input-tx-hash"
                />
              </div>

              <div className="space-y-2">
                <Label>От адреса *</Label>
                <Input
                  placeholder="0x..."
                  value={formData.from_address}
                  onChange={(e) => setFormData({ ...formData, from_address: e.target.value })}
                  className="font-mono"
                  data-testid="input-from"
                />
              </div>

              <div className="space-y-2">
                <Label>К адресу *</Label>
                <Input
                  placeholder="0x..."
                  value={formData.to_address}
                  onChange={(e) => setFormData({ ...formData, to_address: e.target.value })}
                  className="font-mono"
                  data-testid="input-to"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Сумма *</Label>
                  <Input
                    placeholder="1.5"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    data-testid="input-amount"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Токен</Label>
                  <Select 
                    value={formData.token} 
                    onValueChange={(v) => setFormData({ ...formData, token: v })}
                  >
                    <SelectTrigger data-testid="select-token">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tokens.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Сеть</Label>
                  <Select 
                    value={formData.network} 
                    onValueChange={(v) => setFormData({ ...formData, network: v })}
                  >
                    <SelectTrigger data-testid="select-network">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Уровень риска</Label>
                  <Select 
                    value={formData.risk_level} 
                    onValueChange={(v) => setFormData({ ...formData, risk_level: v })}
                  >
                    <SelectTrigger data-testid="select-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Низкий</SelectItem>
                      <SelectItem value="medium">Средний</SelectItem>
                      <SelectItem value="high">Высокий</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Дата транзакции</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP", { locale: ru }) : "Выберите дату"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Заметки</Label>
                <Textarea
                  placeholder="Дополнительная информация..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!formData.tx_hash || !formData.from_address || !formData.to_address || !formData.amount}
                data-testid="save-tx-btn"
              >
                Создать
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить транзакцию?</AlertDialogTitle>
              <AlertDialogDescription>
                Это действие нельзя отменить.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-delete"
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTransactions;
